import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { isEqual } from 'lodash-es';
import { Button } from '@carbon/react';
import { AddComment, Edit, Information } from '@carbon/react/icons';
import {
  type ColumnFiltersState,
  type SortingState,
} from '@tanstack/react-table';
import { DataTable, type DataTableColumnDef } from 'components/data-table';
import { headerWithTooltip } from 'utils/header-with-tooltip';
import { FloatingTooltip } from 'components/floating-tooltip';
import type {
  InitialTableState,
  TableState,
} from 'components/data-table/types';
import { ActivityFeedPanel } from 'components/activity-feed-panel';
import { isActivityFeedPanelOpenAtom } from 'components/activity-feed-panel/atoms';
import type { GeologicIntervalListQueryResponse } from 'types/api-responses';
import { parseLocalDateTime } from 'utils/dates';
import { formatNumber } from 'utils/number';
import {
  createFormationManagementGeologicIntervalsSearchAtom,
  getFormationManagementInitialParams,
  type FormationManagementSearchParams,
} from './atoms';
import { FormationManagementFilters } from './formation-management-filters';
import {
  GeologicIntervalDrawer,
  geologicIntervalDrawerStateAtom,
  openEditGeologicIntervalAtom,
} from './geologic-interval-drawer';
import { formationManagementSearchToUrl } from './search-params-to-url';
import {
  buildFormationManagementInitialTableState,
  columnFiltersToFormationParams,
  FORMATION_MANAGEMENT_SORT_FIELD_MAP,
} from './formation-management-utils';
import styles from './formation-management.module.scss';

type FormationRow = GeologicIntervalListQueryResponse & Record<string, unknown>;

export const FormationManagementContent: FC = () => {
  const history = useHistory();
  const location = useLocation();

  const { searchAtom } = useMemo(
    () => createFormationManagementGeologicIntervalsSearchAtom(),
    []
  );
  const [searchResult, setSearchPayload] = useAtom(searchAtom);

  const [params, setParams] = useState<FormationManagementSearchParams>(() =>
    getFormationManagementInitialParams(25)
  );

  // Built once from URL-derived params; DataTable owns state after mount.
  const initialTableState = useMemo<InitialTableState>(
    () => buildFormationManagementInitialTableState(params),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const [activityRow, setActivityRow] =
    useState<GeologicIntervalListQueryResponse | null>(null);
  const setActivityFeedOpen = useSetAtom(isActivityFeedPanelOpenAtom);
  const openEditInterval = useSetAtom(openEditGeologicIntervalAtom);

  // Highlight the row tied to the open Edit drawer so the user keeps context.
  const drawerState = useAtomValue(geologicIntervalDrawerStateAtom);
  const highlightedRowIds = useMemo<Set<string> | undefined>(() => {
    if (drawerState.mode === 'edit' && drawerState.target) {
      return new Set([String(drawerState.target.id)]);
    }
    return undefined;
  }, [drawerState]);

  const handleIntervalSaved = useCallback(() => {
    setSearchPayload(() => params);
  }, [params, setSearchPayload]);

  useEffect(() => {
    setSearchPayload(() => params);
  }, [params, setSearchPayload]);

  const prevUrlRef = useRef<string | null>(null);
  const urlSyncKey = formationManagementSearchToUrl(params);
  useEffect(() => {
    if (prevUrlRef.current === urlSyncKey) return;
    prevUrlRef.current = urlSyncKey;
    history.replace({
      pathname: location.pathname,
      search: urlSyncKey ? `?${urlSyncKey}` : '',
    });
  }, [history, location.pathname, urlSyncKey]);

  const loadData = useCallback(({ page, pageSize }: TableState) => {
    setParams((current) => {
      if (
        current.page === page &&
        current.pageSize === pageSize &&
        current.paginated === true
      ) {
        return current;
      }
      return { ...current, page, pageSize, paginated: true };
    });
  }, []);

  const handleSortChange = useCallback((sorting: SortingState) => {
    const sort = sorting[0];
    const nextSortAttribute = sort
      ? (FORMATION_MANAGEMENT_SORT_FIELD_MAP[sort.id] ?? sort.id)
      : undefined;
    const nextSortDirection: 'ASC' | 'DESC' | undefined = sort
      ? sort.desc
        ? 'DESC'
        : 'ASC'
      : undefined;
    setParams((current) => {
      if (
        current.sortAttribute === nextSortAttribute &&
        current.sortDirection === nextSortDirection
      ) {
        return current;
      }
      return {
        ...current,
        sortAttribute: nextSortAttribute,
        sortDirection: nextSortDirection,
        page: 1,
      };
    });
  }, []);

  const handleSearchChange = useCallback((globalFilter: string) => {
    const nextText = globalFilter.trim() || undefined;
    setParams((current) => {
      if (current.text === nextText) return current;
      return { ...current, text: nextText, page: 1 };
    });
  }, []);

  const handleColumnFilterChange = useCallback(
    (filters: ColumnFiltersState) => {
      const patch = columnFiltersToFormationParams(filters);
      setParams((current) => {
        const unchanged = (Object.keys(patch) as (keyof typeof patch)[]).every(
          (key) => isEqual(current[key], patch[key])
        );

        if (unchanged) return current;
        return { ...current, page: 1, ...patch };
      });
    },
    []
  );

  const columns = useMemo<DataTableColumnDef<FormationRow>[]>(
    () => [
      {
        accessorKey: 'basinName',
        header: 'Basin',
        size: 200,
        meta: { type: 'enum' },
      },
      {
        accessorKey: 'geologicInterval',
        header: 'Geologic Interval',
        size: 200,
        meta: { type: 'enum' },
      },
      {
        accessorKey: 'age',
        header: 'Age (MYA)',
        size: 110,
        meta: { type: 'integer', align: 'left' },
        cell: ({ getValue }) =>
          formatNumber(
            getValue() as number | null | undefined,
            0,
            0,
            'decimal',
            true,
            '—'
          ),
        enableSorting: false,
      },
      {
        accessorKey: 'lithology',
        header: 'Lithology',
        size: 140,
        meta: { type: 'text' },
        cell: ({ getValue }) => (getValue() as string | null) ?? '—',
        enableSorting: false,
      },
      {
        accessorKey: 'positionalIndex',
        header: headerWithTooltip('Positional Index', () => (
          <span className={styles.headerWithHint}>
            Positional Index
            <span
              className={styles.headerInfoTrigger}
              onClick={(e) => e.stopPropagation()}
            >
              <FloatingTooltip
                placement="bottom"
                className={styles.tooltipCompact}
                content={
                  <>
                    <p>
                      Positional Index is an internal ordering value, not based
                      on any formal stratigraphic numbering system.
                    </p>
                    <p>
                      <b>1</b> = youngest / shallowest (Tertiary) → <b>13</b> =
                      oldest / deepest (Cambrian).
                    </p>
                    <p>
                      New intervals inserted between two existing ones receive
                      the average of their bounds (e.g. between 6 and 7 → 6.5).
                    </p>
                  </>
                }
              >
                <Information size={20} className={styles.headerInfoIcon} />
              </FloatingTooltip>
            </span>
          </span>
        )),
        size: 230,
        meta: { type: 'decimal', align: 'left' },
        cell: ({ getValue }) =>
          formatNumber(
            getValue() as number | null | undefined,
            0,
            4,
            'decimal',
            true,
            '—'
          ),
      },
      {
        accessorKey: 'createdBy',
        header: 'Created By',
        size: 140,
        meta: { type: 'enum' },
        cell: ({ getValue }) => (getValue() as string | null) ?? '—',
        enableSorting: false,
      },
      {
        accessorKey: 'createdAt',
        header: 'Created At',
        size: 130,
        meta: { type: 'date' },
        cell: ({ getValue }) => {
          const v = getValue() as string | undefined;
          return v ? parseLocalDateTime(v) : '—';
        },
      },
      {
        accessorKey: 'updatedAt',
        header: 'Updated At',
        size: 130,
        meta: { type: 'date' },
        cell: ({ getValue }) => {
          const v = getValue() as string | undefined;
          return v ? parseLocalDateTime(v) : '—';
        },
      },
      {
        id: 'namedOperatorTarget',
        header: 'Named Operator Target',
        size: 160,
        enableSorting: false,
        cell: ({ row }) => {
          return row.original.operatorNamingConventions?.length ? 'Yes' : 'No';
        },
      },
      {
        accessorKey: 'displayColor',
        header: 'Display Color',
        size: 160,
        enableSorting: false,
        cell: ({ getValue }) => {
          const color = getValue() as string | null;
          if (!color) return '—';
          return (
            <span className={styles.colorCell}>
              <span
                className={styles.colorSwatch}
                style={{ backgroundColor: color }}
              />
              {color}
            </span>
          );
        },
      },
      {
        id: 'actions',
        header: '',
        size: 100,
        enableSorting: false,
        enablePinning: false,
        enableColumnFilter: false,
        cell: ({ row }) => (
          <div className={styles.actionsCell}>
            <Button
              hasIconOnly
              renderIcon={AddComment}
              iconDescription="Activity"
              onClick={() => {
                setActivityRow(row.original);
                setActivityFeedOpen(true);
              }}
              size="sm"
              kind="ghost"
            />
            <Button
              hasIconOnly
              renderIcon={Edit}
              iconDescription="Edit"
              size="sm"
              kind="ghost"
              onClick={() => openEditInterval(row.original)}
            />
          </div>
        ),
      },
    ],
    [setActivityFeedOpen, openEditInterval]
  );

  const rows = (searchResult.data?.results ?? []) as FormationRow[];

  return (
    <div className={styles.pageWrapper}>
      {searchResult.error ? (
        <div className={styles.errorMessage} role="alert">
          {searchResult.error}
        </div>
      ) : null}
      <div className={styles.tableArea}>
        <div className={styles.tableWrapper}>
          <DataTable<FormationRow>
            data={rows}
            columns={columns}
            getRowId={(row) => String(row.id)}
            initialTableState={initialTableState}
            persistenceKey="formation-management-geologic-intervals"
            config={{
              columnFilters: false,
              columnReordering: false,
              columnPinning: false,
            }}
            isLoading={searchResult.loading}
            error={searchResult.error ?? null}
            scroll={{
              mode: 'paginated',
              serverDriven: true,
              totalCount: searchResult.data?.totalCount ?? 0,
            }}
            onPaginationChange={loadData}
            onSortChange={(s) => handleSortChange(s.sorting)}
            onGlobalSearchChange={(s) => handleSearchChange(s.globalFilter)}
            onFilterChange={(s) => handleColumnFilterChange(s.columnFilters)}
            searchPlaceholder="Search intervals, basins…"
            highlightedRowIds={highlightedRowIds}
            renderFilterPanel={() => (
              <FormationManagementFilters facets={searchResult.data?.facets} />
            )}
          />
        </div>

        <GeologicIntervalDrawer
          facets={searchResult.data?.facets}
          onSaved={handleIntervalSaved}
        />
      </div>

      {activityRow && (
        <ActivityFeedPanel
          key={activityRow.id}
          modelType="GeologicInterval"
          modelId={activityRow.id}
          title="Activity"
          subtitle={`${activityRow.basinName} — ${activityRow.geologicInterval}`}
          commentsHref={activityRow.resources.comments.href}
        />
      )}
    </div>
  );
};
